import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Building2, PiggyBank, Shield, AlertTriangle, Users, IndianRupee,
  Coins, Wallet, Clock, Copy, RefreshCw, ArrowRight, ShieldCheck,
  FileText, Send, GitFork, Scale, CheckCircle2,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from 'recharts';

const fmtINR = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0, notation: (a || 0) > 10000000 ? 'compact' : 'standard' }).format(a || 0);

export default function AdminDashboard() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [funding, setFunding] = useState(null);
  const [chartSeries, setChartSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(new Date());

  const load = async () => {
    try {
      const [s, f, c] = await Promise.all([
        api.get('/admin/stats').catch(() => ({ data: {} })),
        api.get('/funding/admin/overview').catch(() => ({ data: null })),
        api.get('/funding/admin/charts?days=14').catch(() => ({ data: { series: [] } })),
      ]);
      setStats(s.data || {});
      setFunding(f.data);
      setChartSeries(c.data?.series || []);
      setRefreshedAt(new Date());
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);  // live refresh of pending funding + KPIs
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, []);

  const greeting = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Good Morning' : h < 17 ? 'Good Afternoon' : 'Good Evening';
  })();

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success('Address copied'); };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i}><CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader><CardContent><Skeleton className="h-8 w-32" /></CardContent></Card>
        ))}
      </div>
    );
  }

  const riskAlerts = (stats?.high_risk_merchants || 0) + (stats?.extreme_risk_merchants || 0);
  const u = funding?.usdt || {};
  const fc = funding?.funding_counts || {};
  const w = funding?.wallet || {};

  const kpis = [
    { title: 'Total Merchants', value: stats?.total_merchants || 0, sub: `${stats?.approved_merchants || 0} active · ${stats?.pending_kyc || 0} pending`, icon: Building2, accent: 'text-blue-500', glow: false },
    { title: 'Total Wallet Balance', value: fmtINR(w.total_available_balance), sub: `${fmtINR(w.total_pending_balance)} pending`, icon: Wallet, accent: 'text-primary', glow: true },
    { title: 'Total USDT Funded', value: `${u.total_usdt_received || 0} USDT`, sub: `${fmtINR(u.total_inr_credited)} credited`, icon: Coins, accent: 'text-emerald-500', glow: true },
    { title: 'Pending Funding', value: fc.pending || 0, sub: `${fmtINR(fc.pending_inr)} awaiting review`, icon: Clock, accent: 'text-yellow-500', glow: false, link: '/admin/funding', alert: (fc.pending || 0) > 0 },
    { title: 'Approved Merchants', value: stats?.approved_merchants || 0, sub: 'KYC verified', icon: ShieldCheck, accent: 'text-green-500', glow: false },
    { title: 'Pending KYC', value: stats?.pending_kyc || 0, sub: 'Awaiting review', icon: Users, accent: 'text-blue-400', glow: false },
    { title: 'Risk Alerts', value: riskAlerts, sub: `${stats?.high_risk_merchants || 0} high · ${stats?.extreme_risk_merchants || 0} extreme`, icon: AlertTriangle, accent: 'text-red-500', glow: false },
    { title: 'Pending Settlements', value: fmtINR(stats?.total_settlements_pending || 0), sub: 'To be settled', icon: PiggyBank, accent: 'text-purple-500', glow: false },
  ];

  const services = [
    { name: 'Funding System', ok: !!u.funding_enabled, label: u.funding_enabled ? 'Active' : 'Disabled' },
    { name: 'Payout System', ok: true, label: 'Active' },
    { name: 'USDT TRC20', ok: !!u.active_address, label: u.active_address ? 'Configured' : 'No Wallet' },
    { name: 'Database', ok: true, label: 'Connected' },
  ];

  const riskDistribution = [
    { name: 'Low/Medium', value: Math.max(0, (stats?.total_merchants || 0) - riskAlerts) },
    { name: 'High', value: stats?.high_risk_merchants || 0 },
    { name: 'Extreme', value: stats?.extreme_risk_merchants || 0 },
  ];
  const COLORS = ['#10B981', '#F59E0B', '#EF4444'];
  const kycData = [
    { name: 'Pending', count: stats?.pending_kyc || 0 },
    { name: 'Approved', count: stats?.approved_merchants || 0 },
    { name: 'Rejected', count: stats?.rejected_merchants || 0 },
  ];

  const quickActions = [
    { label: 'Review KYC', to: '/admin/kyc-review', icon: FileText },
    { label: 'Review Funding', to: '/admin/funding', icon: Coins },
    { label: 'Review Payouts', to: '/admin/payouts', icon: Send },
    { label: 'Multi-Bank Routing', to: '/admin/routing', icon: GitFork },
    { label: 'Reconciliation', to: '/admin/reconciliation', icon: Scale },
    { label: 'Risk Management', to: '/admin/risk-management', icon: Shield },
  ];

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '—');

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl border border-border/60 bg-card p-6 bg-[radial-gradient(circle_at_100%_0%,rgba(37,99,235,0.08),transparent_55%)]" data-testid="admin-hero">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">{greeting}, {user?.name?.split(' ')[0] || 'Admin'}</h1>
            <p className="text-muted-foreground text-sm mt-1">Here's what's happening across your AwadhPay payment infrastructure today.</p>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-muted-foreground">
              <span>{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
              <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
              <span>Last refresh {refreshedAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" /> All systems operational</span>
            </div>
          </div>
          <Button variant="outline" onClick={load} data-testid="admin-refresh-btn"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
        </div>
      </div>

      {/* Real-time status strip */}
      <div className="flex flex-wrap gap-2" data-testid="system-status">
        {services.map((s) => (
          <div key={s.name} className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs">
            <span className={`h-2 w-2 rounded-full ${s.ok ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="font-medium">{s.name}</span>
            <span className="text-muted-foreground">{s.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-card px-3 py-1.5 text-xs">
          <span className="h-2 w-2 rounded-full bg-slate-400" />
          <span className="font-medium">Payin</span>
          <Badge variant="secondary" className="text-[10px] py-0">Inactive</Badge>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const card = (
            <Card className={`h-full transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-primary/30 ${k.glow ? 'shadow-[0_0_18px_rgba(37,99,235,0.10)]' : ''} ${k.alert ? 'border-yellow-500/50 ring-1 ring-yellow-500/30 cursor-pointer' : ''} ${k.link ? 'cursor-pointer' : ''}`} data-testid={`kpi-${i}`}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
                      {k.title}
                      {k.alert && <span className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />}
                    </p>
                    <p className={`text-2xl font-bold font-mono tabular-nums mt-1 ${k.accent}`}>{k.value}</p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">{k.sub}{k.link && <ArrowRight className="h-3 w-3" />}</p>
                  </div>
                  <div className={`h-9 w-9 rounded-lg bg-muted/60 flex items-center justify-center ${k.accent}`}><k.icon className="h-4.5 w-4.5" strokeWidth={1.75} /></div>
                </div>
              </CardContent>
            </Card>
          );
          return k.link
            ? <Link key={k.title} to={k.link} data-testid={`kpi-link-${k.title.toLowerCase().replace(/ /g, '-')}`}>{card}</Link>
            : <div key={k.title}>{card}</div>;
        })}
      </div>

      {/* USDT funding + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 relative overflow-hidden shadow-[0_0_18px_rgba(16,185,129,0.08)]" data-testid="usdt-funding-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Coins className="w-5 h-5 text-emerald-500" />USDT Funding <Badge variant="secondary" className="text-[10px]">TRON · TRC20</Badge></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Receiving Address</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-sm">{shortAddr(u.active_address)}</span>
                    {u.active_address && <button onClick={() => copy(u.active_address)} data-testid="copy-usdt-addr"><Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" /></button>}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                  <p className="text-xs text-muted-foreground">Current Rate</p>
                  <p className="text-lg font-bold text-primary">1 USDT = ₹{u.current_rate ?? '—'}</p>
                </div>
                <Link to="/admin/funding"><Button size="sm" className="w-full" data-testid="manage-funding-btn">Manage Funding <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border border-border/60"><p className="text-xs text-muted-foreground">Today's Funding</p><p className="text-lg font-bold font-mono">{u.today_usdt || 0} USDT</p></div>
                <div className="p-3 rounded-lg border border-border/60"><p className="text-xs text-muted-foreground">Pending Verify</p><p className="text-lg font-bold font-mono text-yellow-500">{fc.pending || 0}</p></div>
                <div className="p-3 rounded-lg border border-border/60"><p className="text-xs text-muted-foreground">INR Credited</p><p className="text-lg font-bold font-mono text-emerald-500">{fmtINR(u.total_inr_credited)}</p></div>
                <div className="p-3 rounded-lg border border-border/60"><p className="text-xs text-muted-foreground">Total USDT</p><p className="text-lg font-bold font-mono">{u.total_usdt_received || 0}</p></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="quick-actions">
          <CardHeader className="pb-2"><CardTitle className="text-base">Quick Actions</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {quickActions.map((qa) => (
                <Link key={qa.label} to={qa.to} className="flex flex-col items-start gap-2 p-3 rounded-lg border border-border/60 hover:border-primary/40 hover:bg-accent transition-colors" data-testid={`qa-${qa.label.toLowerCase().replace(/ /g, '-')}`}>
                  <qa.icon className="h-4 w-4 text-primary" />
                  <span className="text-xs font-medium leading-tight">{qa.label}</span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funding vs Payout + Wallet Activity charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="chart-funding-payout">
          <CardHeader><CardTitle className="text-lg font-heading">Funding vs Payout Volume (14d)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                <AreaChart data={chartSeries} margin={{ left: 4, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="gFund" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10B981" stopOpacity={0.35} /><stop offset="95%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gPay" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="95%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(d) => (d ? d.slice(5) : '')} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Legend />
                  <Area type="monotone" dataKey="funding" name="Funding ₹" stroke="#10B981" fill="url(#gFund)" strokeWidth={2} />
                  <Area type="monotone" dataKey="payout" name="Payout ₹" stroke="#2563EB" fill="url(#gPay)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="chart-wallet-activity">
          <CardHeader><CardTitle className="text-lg font-heading">Wallet Activity — Credits vs Debits (14d)</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                <BarChart data={chartSeries} margin={{ left: 4, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(d) => (d ? d.slice(5) : '')} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="credits" name="Credits ₹" fill="#10B981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="debits" name="Debits ₹" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">KYC Status Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                <BarChart data={kycData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="hsl(211, 100%, 50%)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-lg font-heading">Risk Distribution</CardTitle></CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                <PieChart>
                  <Pie data={riskDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={5} dataKey="value" label={({ name, value }) => (value > 0 ? `${name}: ${value}` : '')}>
                    {riskDistribution.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              {riskDistribution.map((e, i) => (
                <div key={e.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLORS[i] }} /><span className="text-sm text-muted-foreground">{e.name}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
